import React, { useState, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled from 'styled-components';
import GenericModal from '../../../components/modal/GenericModal';
import InputText from '../../../components/modalComponents/InputText';
import { toast } from 'react-toastify';
import { useGetRequest } from '../../../hooks/useGetRequest/useGetRequest';
import { useManagePostRequest } from '../../../hooks/useManagePostRequest/useManagePostRequest';
import {
    getDetallePostulanteForFeedbackDireccionAPI,
    guardarFeedbackDireccionAPI,
    feedbackGDHAptoAPI,
    feedbackGDHNoAptoAPI,
    observarFeedbackAPI,
    getHistorialObservacionesFeedbackAPI,
} from '../../../consumers/backendApisUrls';


const TIPO_ABIERTA = "ABIERTA";
const TIPO_CERRADA = "CERRADA";

// TP_ConvocatoriaPregunta.TipoPregunta llega crudo desde el backend ('A'/'C',
// ver PreguntaFormModal.jsx) — se traduce acá a las constantes que ya usa
// este componente.
const mapTipoPregunta = (tipoPregunta) => tipoPregunta === "C" ? TIPO_CERRADA : TIPO_ABIERTA;

// Normaliza una PreguntasConvocatoriaItemViewModel (PascalCase) a la forma
// {id, tipo, texto, alternativas} que ya espera el render de abajo. Cada
// alternativa se normaliza a {id, texto}: se necesita el id (IdAlternativaPregunta,
// PK de TP_AlternativaPregunta) para poder enviarlo a GuardarFeedbackDireccion,
// y el texto para mostrarlo en AlternativaToggle.
const mapPregunta = (pregunta) => ({
    id: pregunta.IdConvocatoriaPregunta,
    tipo: mapTipoPregunta(pregunta.TipoPregunta),
    texto: pregunta.TextoPregunta,
    // Respuesta ya registrada (feature Feedback GDH, solo lectura): viene null/undefined
    // mientras el postulante no tenga feedback guardado. Se usan para pre-cargar `respuestas`
    // más abajo, así el render de solo lectura no necesita ninguna lógica aparte.
    respuestaTexto: pregunta.RespuestaTexto,
    idAlternativaRespuesta: pregunta.IdAlternativaPreguntaRespuesta,
    alternativas: (pregunta.Alternativas ?? []).map((alternativa) => ({
        id: alternativa.IdAlternativaPregunta,
        texto: alternativa.TextoAlternativa,
    })),
    // Observación por pregunta (23/09/2026): idFeedbackDetalle es el que hay
    // que mandar de vuelta en ObservarFeedback. estaObservada/motivoObservacion
    // reflejan si YA está marcada (normalmente null mientras PuedeObservar es
    // true, porque el ciclo anterior ya se resolvió antes de volver a este estado).
    idFeedbackDetalle: pregunta.IdFeedbackDetalle,
    estaObservada: pregunta.EstaObservada,
    motivoObservacion: pregunta.MotivoObservacion,
});

const FeedbackDireccion = ({ toggleModal, refreshPage, postulante = {}, soloLectura = false }) => {
    const [executeGet] = useGetRequest();
    const [managePost] = useManagePostRequest();

    const [detalle, setDetalle] = useState(null);
    const [preguntas, setPreguntas] = useState([]);
    const [respuestas, setRespuestas] = useState({});
    const [isSending, setIsSending] = useState(false);

    // Observación por pregunta (23/09/2026), solo lado GDH: qué preguntas marcó
    // localmente antes de confirmar, {[preguntaId]: motivo}. Se resetea al
    // enviar — si GDH necesita observar de nuevo más tarde, es un ciclo nuevo.
    const [preguntasObservadas, setPreguntasObservadas] = useState({});
    const [historial, setHistorial] = useState([]);
    const [verHistorial, setVerHistorial] = useState(false);

    // Trae la ficha real del postulante (cabecera + convocatoria + preguntas
    // con sus alternativas) apenas se abre el modal. En modo GDH trae también
    // el historial de observaciones, en paralelo.
    useEffect(() => {
        if (!postulante?.idPostulante) return;
        let cancelado = false;

        (async () => {
            const [detalleResp, historialResp] = await Promise.all([
                executeGet(getDetallePostulanteForFeedbackDireccionAPI(postulante.idPostulante)),
                soloLectura ? executeGet(getHistorialObservacionesFeedbackAPI(postulante.idPostulante)) : Promise.resolve(null),
            ]);
            if (cancelado || !detalleResp?.data) return;

            const data = detalleResp.data;
            setDetalle(data);
            const preguntasMapeadas = (data.Preguntas ?? []).map(mapPregunta);
            setPreguntas(preguntasMapeadas);
            setRespuestas(preguntasMapeadas.reduce((acc, pregunta) => {
                const valorGuardado = pregunta.tipo === TIPO_ABIERTA ? pregunta.respuestaTexto : pregunta.idAlternativaRespuesta;
                if (valorGuardado !== undefined && valorGuardado !== null) {
                    acc[pregunta.id] = valorGuardado;
                }
                return acc;
            }, {}));
            setHistorial(historialResp?.data ?? []);
        })();

        return () => { cancelado = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [postulante?.idPostulante]);

    const nombrePostulante = detalle?.NombrePostulante ?? postulante.nombrePostulante;
    const dni = detalle?.DNI ?? '';
    const cargoActual = detalle?.CargoActual ?? postulante.desCargo;
    const nombreConvocatoria = detalle?.NombreConvocatoria ?? postulante.nombreConvocatoria;
    const tipoProceso = detalle?.TipoProceso ?? '';
    const fechaInicio = detalle?.FechaInicio ?? '';
    const fechaFin = detalle?.FechaFin ?? '';

    // Observación por pregunta (23/09/2026), lado Director: el Director reabrió
    // un feedback que GDH devolvió con preguntas puntuales por corregir. Distinto
    // de la primera respuesta (PuedeSubsanar solo es true cuando ya existe un
    // feedback en estado Observado) — soloLectura=false en ambos casos, así que
    // hay que distinguirlos para saber qué preguntas quedan editables.
    const enSubsanacion = !soloLectura && detalle?.PuedeSubsanar === true;
    const preguntasObservadasParaSubsanar = enSubsanacion ? preguntas.filter((pregunta) => pregunta.estaObservada) : [];

    const handleRespuestaAbierta = (preguntaId, texto) => {
        setRespuestas(lastData => ({ ...lastData, [preguntaId]: texto }));
    };

    // Para preguntas CERRADA se guarda el id de la alternativa elegida (no el
    // texto) — es lo que espera IdAlternativaPregunta en el backend.
    const handleRespuestaCerrada = (preguntaId, idAlternativa) => {
        setRespuestas(lastData => ({ ...lastData, [preguntaId]: idAlternativa }));
    };

    // Todas las preguntas deben tener respuesta: texto no vacío (ABIERTA) o una
    // alternativa elegida (CERRADA). Mismo criterio y mismo mensaje que valida
    // el backend (GuardarFeedbackDireccion) — validación doble a pedido de Jody.
    // En subsanación (23/09/2026) solo exige esto de las preguntas observadas:
    // las demás ya están bloqueadas, no tiene sentido pedirles nada.
    const faltaAlgunaRespuesta = () => (enSubsanacion ? preguntasObservadasParaSubsanar : preguntas).some((pregunta) => {
        const valor = respuestas[pregunta.id];
        return pregunta.tipo === TIPO_ABIERTA
            ? !valor || !String(valor).trim()
            : valor === undefined || valor === null;
    });

    // Guarda el Feedback del Director: valida en cliente, arma el contrato
    // { IdPostulante, Respuestas: [{ IdConvocatoriaPregunta, RespuestaTexto,
    // IdAlternativaPregunta }] } y llama a GuardarFeedbackDireccion. Al
    // terminar, refresca la bandeja y cierra la ficha (el envío es único en la
    // primera vez; en subsanación se puede volver a llamar mientras GDH siga
    // devolviendo preguntas — ver 3.2 del diseño).
    //
    // En subsanación solo se manda lo observado (no todas las preguntas): el
    // backend ignora igual cualquier respuesta que no corresponda a una
    // pregunta con EstaObservada=1, pero mandar de más solo agrega ruido.
    const handleGuardarYEnviar = async () => {
        if (!postulante?.idPostulante || isSending) return;

        const preguntasAEnviar = enSubsanacion ? preguntasObservadasParaSubsanar : preguntas;

        if (preguntasAEnviar.length === 0 || faltaAlgunaRespuesta()) {
            toast.error('Todas las preguntas deben ser respondidas');
            return;
        }

        const payload = {
            IdPostulante: postulante.idPostulante,
            Respuestas: preguntasAEnviar.map((pregunta) => ({
                IdConvocatoriaPregunta: pregunta.id,
                RespuestaTexto: pregunta.tipo === TIPO_ABIERTA ? (respuestas[pregunta.id] ?? '') : null,
                IdAlternativaPregunta: pregunta.tipo === TIPO_CERRADA ? (respuestas[pregunta.id] ?? null) : null,
            })),
        };

        setIsSending(true);
        try {
            await managePost(
                guardarFeedbackDireccionAPI(),
                payload,
                async () => {
                    toast.success(enSubsanacion
                        ? 'Correcciones enviadas a GDH para su revisión.'
                        : 'Feedback registrado exitosamente');
                    if (refreshPage) await refreshPage();
                    toggleModal();
                },
                false
            );
        } finally {
            setIsSending(false);
        }
    };

    // Registra la decisión Apto (esApto=true) o No apto (esApto=false) de GDH
    // contra FeedbackGDH_Apto/_NoApto. Mismo patrón que RevisionInicialGDH.jsx:
    // showToastSuccess=false porque el toast automático de useManagePostRequest
    // busca response.data.Message y el backend devuelve "mensaje" (minúscula) —
    // se muestra el mensaje de éxito a mano. Al terminar, refresca la bandeja y
    // cierra la ficha.
    const handleDecisionGDH = async (esApto, motivoNoApto, motivoNoAptoOtro) => {
        if (!postulante?.idPostulante || isSending) return;

        setIsSending(true);
        const api = esApto ? feedbackGDHAptoAPI : feedbackGDHNoAptoAPI;
        // MotivoNoApto/MotivoNoAptoOtro: Feedback Dirección resuelve su plantilla de
        // correo solo por etapa (único resultado negativo posible ahí: "no tuvo el VB
        // del líder/director", ver resolverTemplateCodeResultado) — GDH no elige motivo
        // acá, a diferencia de Revisión Inicial. Siempre null en esta pantalla.
        const payload = {
            IdPostulante: postulante.idPostulante,
            MotivoNoApto: esApto ? null : motivoNoApto,
            MotivoNoAptoOtro: esApto ? null : motivoNoAptoOtro,
        };

        try {
            await managePost(
                api(),
                payload,
                async () => {
                    toast.success(esApto
                        ? 'Postulante marcado como Apto exitosamente'
                        : 'Postulante marcado como No apto exitosamente');
                    if (refreshPage) await refreshPage();
                    toggleModal();
                },
                false
            );
        } finally {
            setIsSending(false);
        }
    };

    const handleAptoGDH = () => handleDecisionGDH(true);
    // 29/09/2026: revertido — el modal de motivo (MotivoNoAptoModal) queda exclusivo
    // de Revisión Inicial GDH (la primera validación de GDH, único punto con catálogo
    // de motivos). Del feedback en adelante no se pide motivo.
    const handleNoAptoGDH = () => handleDecisionGDH(false);

    // Observación por pregunta (23/09/2026): marca/desmarca una pregunta
    // puntual. Al desmarcar se descarta el motivo que se hubiera escrito —
    // no tiene sentido conservarlo si ya no se va a enviar.
    const toggleObservarPregunta = (preguntaId) => {
        setPreguntasObservadas((prev) => {
            const next = { ...prev };
            if (preguntaId in next) {
                delete next[preguntaId];
            } else {
                next[preguntaId] = '';
            }
            return next;
        });
    };

    const handleMotivoObservacion = (preguntaId, motivo) => {
        setPreguntasObservadas((prev) => ({ ...prev, [preguntaId]: motivo }));
    };

    // CA-19/CA-20 (HU-TR-05) / CA-18 (HU-PR-04): exige al menos una pregunta
    // marcada, cada una con su motivo — mismo criterio que valida el backend
    // (doble validación, como el resto del módulo).
    const handleEnviarObservaciones = async () => {
        if (!detalle?.IdFeedback || isSending) return;

        const idsMarcadas = Object.keys(preguntasObservadas);
        if (idsMarcadas.length === 0) {
            toast.warn('Debe observar al menos una pregunta.');
            return;
        }
        if (idsMarcadas.some((id) => !preguntasObservadas[id]?.trim())) {
            toast.warn('Indique el motivo de cada pregunta observada.');
            return;
        }

        const payload = {
            IdFeedback: detalle.IdFeedback,
            Preguntas: preguntas
                .filter((pregunta) => pregunta.id in preguntasObservadas)
                .map((pregunta) => ({
                    IdFeedbackDetalle: pregunta.idFeedbackDetalle,
                    Motivo: preguntasObservadas[pregunta.id].trim(),
                })),
        };

        setIsSending(true);
        try {
            await managePost(
                observarFeedbackAPI(),
                payload,
                async () => {
                    toast.success('Observaciones enviadas al Director.');
                    setPreguntasObservadas({});
                    if (refreshPage) await refreshPage();
                    toggleModal();
                },
                false
            );
        } finally {
            setIsSending(false);
        }
    };

    return (
        <GenericModal showModal={toggleModal} titulo={soloLectura ? "Feedback GDH" : "Feedback Dirección"} width="800px">
            <div className="modal-body pt-3">

                {/* CA-16 (HU-TR-05) / CA-20 (HU-PR-04): lo primero que el Director
                    necesita ver al reabrir un feedback observado — qué le devolvieron
                    y cuántas preguntas hay que corregir. */}
                {enSubsanacion && (
                    <ObservacionBox className="mb-3">
                        <div className="titulo">
                            <FontAwesomeIcon icon="exclamation-circle" className="mr-2" />
                            GDH observó {preguntasObservadasParaSubsanar.length === 1
                                ? '1 pregunta'
                                : `${preguntasObservadasParaSubsanar.length} preguntas`} de su respuesta
                        </div>
                        <small>Corrija lo indicado en cada pregunta marcada y vuelva a enviar.</small>
                    </ObservacionBox>
                )}

                <div className="displayFlexWrap">
                    <div className="col-md-6"><InputText label="Colaborador" name="colaborador" value={nombrePostulante} direction="row" isDisabled /></div>
                    <div className="col-md-6"><InputText label="DNI" name="dni" value={dni} direction="row" isDisabled /></div>
                    <div className="col-md-6"><InputText label="Cargo" name="cargo" value={cargoActual} direction="row" isDisabled /></div>
                </div>

                <hr />

                <div className="displayFlexWrap">
                    <div className="col-md-6"><InputText label="Convocatoria" name="convocatoria" value={nombreConvocatoria} direction="row" isDisabled /></div>
                    <div className="col-md-6"><InputText label="Tipo Proceso" name="tipoProceso" value={tipoProceso} direction="row" isDisabled /></div>
                    <div className="col-md-6"><InputText label="Fecha Inicio" name="fechaInicio" value={fechaInicio} direction="row" isDisabled /></div>
                    <div className="col-md-6"><InputText label="Fecha Fin" name="fechaFin" value={fechaFin} direction="row" isDisabled /></div>
                </div>

                <hr />

                <PreguntasBox>
                    <div className="preguntas-titulo">Preguntas</div>
                    {preguntas.map((pregunta, index) => {
                        // Subsanación: solo las preguntas que GDH marcó quedan editables;
                        // el resto se ve exactamente como en modo solo lectura (CA-20/CA-23).
                        const bloqueadaPorSubsanacion = enSubsanacion && !pregunta.estaObservada;
                        const esEditable = !soloLectura && !bloqueadaPorSubsanacion;

                        return (
                            <div className="pregunta-item" key={pregunta.id}>
                                <label className="modal-label align-items-center">
                                    <span className="pregunta-texto">{`#${index + 1} ${pregunta.texto}`}</span>
                                    {soloLectura && detalle?.PuedeObservar && (
                                        <ObservarCheckLabel className="ml-3">
                                            <input type="checkbox"
                                                checked={pregunta.id in preguntasObservadas}
                                                onChange={() => toggleObservarPregunta(pregunta.id)} />
                                            Observar
                                        </ObservarCheckLabel>
                                    )}
                                </label>
                                {/* CA-24 (HU-TR-05) / equivalente HU-PR-04: el motivo de GDH,
                                    justo encima de la pregunta que hay que corregir. */}
                                {enSubsanacion && pregunta.estaObservada && (
                                    <MotivoObservacionBox className="mb-2">{pregunta.motivoObservacion}</MotivoObservacionBox>
                                )}
                                {pregunta.tipo === TIPO_ABIERTA
                                    ? (
                                        esEditable
                                            ? (
                                                <input type="text" className="form-control"
                                                    value={respuestas[pregunta.id] ?? ""}
                                                    onChange={(e) => handleRespuestaAbierta(pregunta.id, e.target.value)} />
                                            )
                                            : <InputText name={`pregunta-${pregunta.id}`} value={respuestas[pregunta.id] ?? ""} isDisabled />
                                    )
                                    : (
                                        <AlternativaToggle>
                                            {pregunta.alternativas.map((alternativa) => (
                                                <button type="button" key={alternativa.id}
                                                    disabled={!esEditable}
                                                    className={respuestas[pregunta.id] === alternativa.id ? "active" : ""}
                                                    onClick={esEditable ? () => handleRespuestaCerrada(pregunta.id, alternativa.id) : undefined}>
                                                    {alternativa.texto}
                                                </button>
                                            ))}
                                        </AlternativaToggle>
                                    )
                                }
                                {soloLectura && pregunta.id in preguntasObservadas && (
                                    <textarea className="form-control mt-2" rows="2"
                                        placeholder="Motivo de la observación"
                                        value={preguntasObservadas[pregunta.id]}
                                        onChange={(e) => handleMotivoObservacion(pregunta.id, e.target.value)} />
                                )}
                            </div>
                        );
                    })}
                </PreguntasBox>

                {/* Observación por pregunta: el historial solo aporta a partir de la
                    primera observación — a diferencia del ciclo de Firma (que siempre
                    tiene al menos un envío inicial), acá cada fila es un evento real. */}
                {soloLectura && historial.length > 0 && (
                    <HistorialBox className="mb-3">
                        <button type="button" className="btn btn-link p-0"
                            onClick={() => setVerHistorial(!verHistorial)}>
                            <FontAwesomeIcon icon={verHistorial ? 'chevron-down' : 'chevron-right'} className="mr-2" />
                            Historial de observaciones ({historial.length})
                        </button>

                        {verHistorial && (
                            <table className="table table-sm mt-2 mb-0">
                                <thead>
                                    <tr>
                                        <th style={{ width: '55px' }}>Ciclo</th>
                                        <th>Pregunta</th>
                                        <th>Motivo</th>
                                        <th style={{ width: '150px' }}>Observó</th>
                                        <th style={{ width: '150px' }}>Subsanado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {historial.map((h) => (
                                        <tr key={h.IdFeedbackObservacion}>
                                            <td>N° {h.NroCiclo}</td>
                                            <td>{h.TextoPregunta}</td>
                                            <td>{h.MotivoObservacion}</td>
                                            <td>
                                                {h.NombreGdh ?? '—'}
                                                {h.FechaObservacion && <small className="d-block text-muted">{h.FechaObservacion}</small>}
                                            </td>
                                            <td>{h.FechaSubsanacion || 'Pendiente'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </HistorialBox>
                )}

                <div className="d-flex justify-content-center mt-4">
                    {soloLectura
                        ? (
                            detalle?.PuedeObservar ? (
                                // Registran la decisión de GDH contra FeedbackGDH_Apto/_NoApto
                                // (avanza TP_Postulante a la etapa "Feedback Dirección" con
                                // Estado Apto/No apto, ver handleDecisionGDH), o le devuelve
                                // preguntas puntuales al Director (handleEnviarObservaciones).
                                <>
                                    <button type="button" disabled={isSending} className="btn btn-success mr-3"
                                        onClick={handleAptoGDH}>
                                        Apto {isSending && (<FontAwesomeIcon icon='spinner' transform="left-9" className="spinner ml-3" />)}
                                    </button>
                                    <button type="button" disabled={isSending} className="btn btn-danger mr-3"
                                        onClick={handleNoAptoGDH}>
                                        No Apto {isSending && (<FontAwesomeIcon icon='spinner' transform="left-9" className="spinner ml-3" />)}
                                    </button>
                                    <button type="button" disabled={isSending || Object.keys(preguntasObservadas).length === 0}
                                        className="btn btn-warning" onClick={handleEnviarObservaciones}>
                                        Enviar observaciones {isSending && (<FontAwesomeIcon icon='spinner' transform="left-9" className="spinner ml-3" />)}
                                    </button>
                                </>
                            ) : (
                                <AvisoBox>
                                    {detalle?.IdEstadoDireccion == null
                                        ? 'El Director todavía no envió el feedback de este postulante.'
                                        : 'Este feedback ya fue evaluado, o está en manos del Director para su subsanación.'}
                                </AvisoBox>
                            )
                        )
                        : (
                            <button type="button" disabled={isSending} className="btn generic-button-buscador" onClick={handleGuardarYEnviar}>
                                {enSubsanacion ? 'Enviar corrección' : 'Guardar y enviar'}
                                {isSending && (<FontAwesomeIcon icon='spinner' transform="left-9" className="spinner ml-3" />)}
                            </button>
                        )
                    }
                </div>
            </div>
        </GenericModal>
    )
}

export default FeedbackDireccion

// Caja con borde redondeado que envuelve la sección "Preguntas" del boceto.
const PreguntasBox = styled.div`
    border: 1px solid #dee2e6;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 10px;

    .preguntas-titulo {
        font-weight: bold;
        margin-bottom: 15px;
    }

    .pregunta-item {
        margin-bottom: 20px;
    }

    .pregunta-item:last-child {
        margin-bottom: 0;
    }

     .pregunta-item label.modal-label {
        flex-wrap: wrap;
        row-gap: 4px;
    }

    .pregunta-texto {
        flex: 1 1 auto;
        min-width: 0;
    }
`;

// Extensión de `SiNoToggle` (RevisionInicialGDH.jsx) de 2 a N alternativas —
// mismo estilo de pill, la alternativa activa con fondo azul y texto blanco.
// 29/09/2026 (a pedido de Jody): el diseño anterior (un solo borde compartido +
// separadores "border-right" entre botones) asumía una sola fila — al pasar a
// 2+ filas (alternativas largas) las filas quedaban pegadas sin espacio entre
// ellas y se veían superpuestas. Se reemplaza por píldoras independientes con
// `gap` entre sí: cada botón tiene su propio borde completo, ocupa el ancho
// que su texto necesita (flex-shrink: 0 evita que se corte) y el wrap a nueva
// fila queda prolijo sin depender de bordes compartidos.
const AlternativaToggle = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;

    button {
        border: 1px solid #3a87c8;
        border-radius: 15px;
        outline: none !important;
        background-color: #ffffff;
        color: #3a87c8;
        padding: 4px 20px;
        font-family: "GothamLight", system-ui, sans-serif;
        flex-shrink: 0;
    }

    button.active {
        background-color: #3a87c8;
        color: #ffffff;
    }

    /* Subsanación (23/09/2026): preguntas no observadas quedan bloqueadas. */
    button:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

// Checkbox "Observar" junto al enunciado de cada pregunta (modo GDH).
const ObservarCheckLabel = styled.label`
    display: inline-flex;
    align-items: center;
    margin: 0;
    color: #b8860b;
    font-size: 12px;
    font-weight: normal;
    cursor: pointer;

    input {
        margin-right: 4px;
    }
`;

const HistorialBox = styled.div`
    border: 1px solid #dee2e6;
    border-radius: 8px;
    padding: 12px;
    background: #f8f9fa;
`;

const AvisoBox = styled.div`
    border: 1px solid #ffe08a;
    background: #fff9e6;
    border-radius: 8px;
    padding: 14px;
    color: #8a6d3b;
    font-size: 14px;
    text-align: center;
    width: 100%;
`;

// Banner de subsanación (lado Director) — mismo criterio visual que
// FirmaColaborador.jsx (HU-TR-07): rojo/observación, primero que todo lo demás.
const ObservacionBox = styled.div`
    border: 1px solid #f5c2c7;
    background: #fdeaea;
    border-radius: 8px;
    padding: 14px;

    .titulo {
        font-weight: bold;
        color: #b02a2a;
        margin-bottom: 6px;
    }

    small {
        color: #8a6d3b;
    }
`;

// Motivo puntual de una pregunta observada, justo encima de su input editable.
const MotivoObservacionBox = styled.div`
    border-left: 3px solid #f5a3a3;
    background: #fdeaea;
    padding: 6px 10px;
    color: #b02a2a;
    font-size: 12px;
    white-space: pre-wrap;
`;
